// Kandidáti Recruitment Focusu — Discover fáze: AI i manuální výběr (max 11).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, MagnifyingGlass, Binoculars, Eye, Prohibit, Trash, ArrowSquareOut, Star, Medal } from "@phosphor-icons/react";
import { findPlayers, listCandidates, addCandidate, patchCandidate, removeCandidate, getFocus, CANDIDATE_STATUS_LABELS } from "../../lib/lab-api";
import ScoutReportCard from "./ScoutReportCard";
import FocusFinalists from "./FocusFinalists";
import FocusMonitor from "./FocusMonitor";

const STATUS_CLS = {
    DISCOVERED: "border-white/30 text-white/70",
    SCOUTING: "border-fa-mint/60 text-fa-mint bg-fa-mint/10",
    MONITORED: "border-fa-sky/60 text-fa-sky bg-fa-sky/10",
    REJECTED: "border-red-400/40 text-red-300/70",
    FINALIST: "border-fa-yellow/60 text-fa-yellow bg-fa-yellow/10",
    SELECTED: "border-fa-mint text-fa-mint bg-fa-mint/20 font-bold",
    ALTERNATIVE: "border-white/40 text-white/80",
};
const SOURCE_LABEL = {
    manual_search: "ručně (vyhledávač)", ai_recruiter: "AI Recruiter", player_pool: "Player Pool",
};

export default function FocusCandidates({ focus, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [focusDoc, setFocusDoc] = useState(focus);
    const [tab, setTab] = useState("discover");
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [trendFilter, setTrendFilter] = useState("");

    const load = useCallback(() => {
        listCandidates(focus.id).then(setData).catch(() => toast.error("Načtení kandidátů selhalo"));
        getFocus(focus.id).then((d) => setFocusDoc(d.item)).catch(() => {});
    }, [focus.id]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        const t = setTimeout(() => {
            findPlayers({ search: q, limit: 8 }).then((d) => setResults(d.results || [])).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const add = async (p) => {
        setBusy(true);
        try {
            const d = await addCandidate(focus.id, { fpl_id: p.fpl_id, source: "manual_search" });
            toast.success(`${p.name} → kandidát (${d.active_count}/${d.max_active})`);
            setQ(""); setResults([]);
            load(); onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
        finally { setBusy(false); }
    };

    const setStatus = async (c, status) => {
        try {
            await patchCandidate(focus.id, c.id, { status });
            load(); onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Změna selhala"); }
    };

    const remove = async (c) => {
        try { await removeCandidate(focus.id, c.id); toast.success(`${c.name} odebrán`); load(); onChanged?.(); }
        catch { toast.error("Odebrání selhalo"); }
    };

    const toggleTip = async (c) => {
        try { await patchCandidate(focus.id, c.id, { is_my_tip: !c.is_my_tip }); load(); }
        catch { toast.error("Změna selhala"); }
    };

    const items = data?.items || [];
    const visibleItems = items.filter((c) =>
        !trendFilter ||
        (trendFilter === "rising" && c.gs_trend_bucket > 0) ||
        (trendFilter === "watch" && c.gs_trend != null && c.gs_trend_bucket === 0) ||
        (trendFilter === "falling" && c.gs_trend_bucket < 0));
    const active = data?.active_count ?? 0;
    const max = data?.max_active ?? 11;
    const scoutItems = items.filter((c) => c.status === "SCOUTING");
    const finItems = items.filter((c) => ["FINALIST", "SELECTED", "ALTERNATIVE"].includes(c.status));
    const TABS = [
        { key: "discover", label: `Discover (${active})` },
        { key: "scout", label: `Scout (${scoutItems.length})` },
        { key: "finalists", label: `Finalisté (${finItems.length})` },
        { key: "monitor", label: "Monitor" },
    ];

    return (
        <div className="border border-fa-sky/40 bg-black/60 p-4 space-y-3" data-testid="focus-candidates-panel">
            <div className="flex items-center gap-2 flex-wrap">
                <Binoculars size={14} className="text-fa-sky" />
                <div className="font-display font-black uppercase text-sm">Kandidáti — {focus.title}</div>
                <span className="font-mono text-[10px] tabular-nums px-2 py-0.5 border border-fa-sky/40 text-fa-sky" data-testid="candidates-count">
                    {active}/{max}
                </span>
                <span className="font-mono text-[9px] text-white/40">Discover → vyber ~8 do scoutingu</span>
                <button type="button" onClick={onClose} className="ml-auto text-white/40 hover:text-white" data-testid="candidates-close">
                    <X size={14} />
                </button>
            </div>

            <div className="flex gap-1" data-testid="focus-phase-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`focus-tab-${t.key}`}
                            className={`font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${tab === t.key ? "border-fa-sky bg-fa-sky/15 text-fa-sky" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "discover" && (<>
            <div className="relative max-w-md">
                <div className="flex items-center gap-2 bg-black/40 border border-white/25 px-3 py-2">
                    <MagnifyingGlass size={12} className="text-white/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} disabled={active >= max}
                           placeholder={active >= max ? `Longlist je plný (${max})` : "Hledat hráče a přidat ručně…"}
                           className="flex-1 bg-transparent font-mono text-[12px] text-white focus:outline-none placeholder:text-white/40"
                           data-testid="candidates-search" />
                </div>
                {results.length > 0 && q && (
                    <div className="absolute z-30 left-0 right-0 top-full bg-[#111] border border-white/15 max-h-56 overflow-y-auto">
                        {results.map((p) => {
                            const taken = items.some((c) => c.fpl_id === p.fpl_id);
                            return (
                                <button key={p.fpl_id} type="button" disabled={taken || busy}
                                        onClick={() => add(p)} data-testid={`candidates-result-${p.fpl_id}`}
                                        className="w-full text-left px-3 py-1.5 font-mono text-[11px] hover:bg-white/5 disabled:opacity-40 flex justify-between">
                                    <span className="font-bold">{p.name}</span>
                                    <span className="text-white/45">{p.team} · {p.position} · £{p.price}{taken ? " · už kandidát" : ""}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {items.length === 0 && (
                <p className="font-mono text-[11px] text-white/45 border border-white/10 bg-black/30 p-4" data-testid="candidates-empty">
                    Zatím žádní kandidáti — přidej je vyhledávačem výše, nebo spusť AI Recruitera a zařaď jeho doporučení.
                </p>
            )}

            {items.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="candidates-trend-filter">
                    <span className="font-mono text-[8px] uppercase tracking-widest text-white/40">Redakční trend:</span>
                    {[["", "Vše"], ["rising", "↑ Rising"], ["watch", "→ Watch"], ["falling", "↓ Falling"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setTrendFilter(k)} data-testid={`candidates-trend-${k || "all"}`}
                                className={`font-mono text-[9px] px-2 py-1 border ${trendFilter === k
                                    ? (k === "rising" ? "border-fa-mint bg-fa-mint/15 text-fa-mint" : k === "falling" ? "border-red-400 bg-red-400/15 text-red-300" : "border-fa-sky bg-fa-sky/15 text-fa-sky")
                                    : "border-white/15 text-white/50 hover:border-white/40"}`}>
                            {l}
                        </button>
                    ))}
                    {trendFilter && (
                        <span className="font-mono text-[9px] text-white/40">{visibleItems.length} z {items.length}</span>
                    )}
                </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                {visibleItems.map((c) => (
                    <div key={c.id} className={`border p-3 space-y-1.5 ${c.status === "REJECTED" ? "border-white/10 opacity-50" : "border-white/15 bg-[#0d0d0d]"}`}
                         data-testid={`candidate-card-${c.fpl_id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <Link to={`/players/${c.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint">{c.name}</Link>
                                <div className="font-mono text-[9px] text-white/50">{c.team} · {c.position} · £{c.price}</div>
                            </div>
                            {c.original_fit_score != null && (
                                <div className="text-right">
                                    <div className="font-display font-black text-lg text-fa-mint tabular-nums">{Math.round(c.original_fit_score)}</div>
                                    <div className="font-mono text-[7px] uppercase text-white/40">Original Fit</div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap font-mono text-[8px] uppercase tracking-widest">
                            <span className={`px-1.5 py-0.5 border ${STATUS_CLS[c.status]}`}>{CANDIDATE_STATUS_LABELS[c.status]}</span>
                            <span className="text-white/35">{SOURCE_LABEL[c.discovery_source] || c.discovery_source}</span>
                            {c.gs_trend != null && (
                                <span data-testid={`candidate-trend-${c.fpl_id}`}
                                      className={`px-1.5 py-0.5 border ${c.gs_trend_bucket > 0 ? "border-fa-mint/60 text-fa-mint" : c.gs_trend_bucket < 0 ? "border-red-400/50 text-red-300" : "border-fa-sky/50 text-fa-sky"}`}>
                                    {c.gs_trend_bucket > 0 ? "↑ rising" : c.gs_trend_bucket < 0 ? "↓ falling" : "→ watch"}
                                </span>
                            )}
                        </div>
                        {c.discovery_reason && <p className="font-mono text-[9px] text-white/50">{c.discovery_reason}</p>}
                        <div className="flex gap-1 flex-wrap pt-0.5">
                            <button type="button" onClick={() => toggleTip(c)} title="Můj tip" data-testid={`candidate-tip-${c.fpl_id}`}
                                    className={`font-mono text-[8px] px-1.5 py-1 border flex items-center gap-1 ${c.is_my_tip ? "border-fa-yellow text-fa-yellow bg-fa-yellow/10" : "border-white/15 text-white/35 hover:text-fa-yellow"}`}>
                                <Star size={9} weight={c.is_my_tip ? "fill" : "regular"} />
                            </button>
                            {c.status !== "SCOUTING" && c.status !== "REJECTED" && (
                                <button type="button" onClick={() => setStatus(c, "SCOUTING")} data-testid={`candidate-scout-${c.fpl_id}`}
                                        className="font-mono text-[8px] uppercase px-2 py-1 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black flex items-center gap-1">
                                    <Binoculars size={9} /> Do scoutingu
                                </button>
                            )}
                            {c.status !== "MONITORED" && c.status !== "REJECTED" && (
                                <button type="button" onClick={() => setStatus(c, "MONITORED")} data-testid={`candidate-monitor-${c.fpl_id}`}
                                        className="font-mono text-[8px] uppercase px-2 py-1 border border-fa-sky/50 text-fa-sky hover:bg-fa-sky hover:text-black flex items-center gap-1">
                                    <Eye size={9} /> Sledovat
                                </button>
                            )}
                            {c.status !== "REJECTED" ? (
                                <button type="button" onClick={() => setStatus(c, "REJECTED")} data-testid={`candidate-reject-${c.fpl_id}`}
                                        className="font-mono text-[8px] uppercase px-2 py-1 border border-white/15 text-white/40 hover:text-red-300 hover:border-red-300/40 flex items-center gap-1">
                                    <Prohibit size={9} /> Vyřadit
                                </button>
                            ) : (
                                <button type="button" onClick={() => setStatus(c, "DISCOVERED")} data-testid={`candidate-restore-${c.fpl_id}`}
                                        className="font-mono text-[8px] uppercase px-2 py-1 border border-white/15 text-white/50 hover:text-white">
                                    Vrátit
                                </button>
                            )}
                            <button type="button" onClick={() => remove(c)} title="Smazat z focusu" data-testid={`candidate-delete-${c.fpl_id}`}
                                    className="font-mono text-[8px] px-1.5 py-1 border border-white/10 text-white/25 hover:text-red-400 ml-auto">
                                <Trash size={9} />
                            </button>
                            <Link to={`/players/${c.fpl_id}`} className="font-mono text-[8px] px-1.5 py-1 border border-white/10 text-white/35 hover:text-fa-mint">
                                <ArrowSquareOut size={9} />
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
            </>)}

            {tab === "scout" && (
                <div className="space-y-2" data-testid="focus-scout-tab">
                    {scoutItems.length === 0 && (
                        <p className="font-mono text-[11px] text-white/45 border border-white/10 bg-black/30 p-4">
                            Nikdo není ve scoutingu — v Discover fázi dej kandidátům „Do scoutingu".
                        </p>
                    )}
                    {scoutItems.map((c) => (
                        <ScoutReportCard key={c.id} focus={focusDoc} candidate={c} onChanged={() => { load(); onChanged?.(); }} />
                    ))}
                </div>
            )}

            {tab === "finalists" && (
                <FocusFinalists focus={focusDoc} candidates={items} onChanged={() => { load(); onChanged?.(); }} />
            )}

            {tab === "monitor" && (
                <FocusMonitor focus={focusDoc} candidates={items} onChanged={() => { load(); onChanged?.(); }} />
            )}
        </div>
    );
}
